import Pagination from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import {
  Activity,
  AlertTriangle,
  Award,
  Calendar,
  CheckCircle,
  ExternalLink,
  Eye,
  History,
  List,
  Mail,
  Package,
  RefreshCw,
  Search,
  Shield,
  Trash2,
  TrendingUp,
  User,
  Users,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from '@/components/ui/Toast';
import { useSearchParams } from 'react-router-dom';
import { certificationAssignmentService } from '@/services/certification-assignment.service';
import { userService } from '@/services/user.service';
import { useQuery } from '@tanstack/react-query';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import { useForm } from 'react-hook-form';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { CERTIFICATION_ASSIGNMENT_ANALYTICS_PERMISSION, CERTIFICATION_ASSIGNMENT_PERMISSION } from '@/constants/constant-permissions';

// Types
interface OverviewAnalytics {
  totalAssigned: number;
  completionRate: number;
  avgProgress: number;
  avgTimeSpent: number;
  engagementScore: number;
  onTimeRate: number;
  periodComparison: {
    totalAssigned: { change: string };
    completionRate: { change: string };
  };
}

interface Assignment {
  _id: string;
  certification_ids: string[];
  certifications: AssignmentCertification[];
  assignment_type: string;
  is_mandatory: boolean;
  deadline_days?: number;
  deadline_date?: string;
  status: string;
  assigned_to_details: {
    users: any[];
    groups: any[];
    roles: any[];
    total_count?: number;
  };
  assigned_by_details: {
    _id?: string;
    name: string;
    email: string;
  };
  assigned_by?: string;
  send_notification?: boolean;
  enable_automatic_reminder?: boolean;
  reminder_frequency_days?: number;
  total_assignees?: number;
  completed_count?: number;
  in_progress_count?: number;
  not_started_count?: number;
  completion_percentage?: number;
  created_at?: string;
  updated_at?: string;
}

interface AssignmentCertification {
  name: string;
  _id: string;
  title: string;
}

interface AssignmentListResponse {
  data: Assignment[];
  total: number;
  page: number;
  limit: number;
  summary?: {
    total_users_assigned: number;
    total_groups_assigned: number;
    total_roles_assigned: number;
  };
}

interface BulkImportHistoryItem {
  _id: string;
  import_date: string;
  imported_by: string;
  status: string;
  total_records: number;
  success_count: number;
  failed_count: number;
  results?: any[];
}

interface BulkImportHistoryResponse {
  data: BulkImportHistoryItem[];
  pagination: {
    total: number;
    total_pages: number;
  };
}

interface FilterFormData {
  selectedCertification: string;
  selectedGranularity: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  bulkHistoryStatusFilter: string;
}

export default function CertificationAssignmentAnalytics() {
  // Permission guards
  usePermissionRouteAccess([
    CERTIFICATION_ASSIGNMENT_ANALYTICS_PERMISSION.CERTIFICATION_ASSIGNMENT_ANALYTICS_READ,
    CERTIFICATION_ASSIGNMENT_ANALYTICS_PERMISSION.CERTIFICATION_ASSIGNMENT_ANALYTICS_WRITE,
  ]);
  const canWrite = useHasPermission([CERTIFICATION_ASSIGNMENT_ANALYTICS_PERMISSION.CERTIFICATION_ASSIGNMENT_ANALYTICS_WRITE]);
  const canAssign = useHasPermission([CERTIFICATION_ASSIGNMENT_PERMISSION.CERTIFICATION_ASSIGNMENT_WRITE]);

  const [activeTab, setActiveTab] = useState<'users' | 'groups' | 'roles' | 'bulk-history'>('users');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, DEFAULT_DEBOUNCE_DELAY); // Use standard debounce delay
  const [filterStatus] = useState<string>('all');

  // Initialize react-hook-form
  const { control, watch } = useForm<FilterFormData>({
    defaultValues: {
      selectedCertification: 'all',
      selectedGranularity: 'monthly',
      bulkHistoryStatusFilter: 'all'
    }
  });

  // Watch form values
  const selectedCertification = watch('selectedCertification');
  const selectedGranularity = watch('selectedGranularity');
  const bulkHistoryStatusFilter = watch('bulkHistoryStatusFilter');

  // Loading states
  const [, setLoading] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [userTableLoading, setUserTableLoading] = useState(false);
  const [groupTableLoading, setGroupTableLoading] = useState(false);
  const [roleTableLoading, setRoleTableLoading] = useState(false);
  const [bulkHistoryLoading, setBulkHistoryLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [revokeModal, setRevokeModal] = useState<{ assignmentId: string; type: 'GROUP' | 'ROLE'; name: string } | null>(null);
  const [revokeLoading, setRevokeLoading] = useState(false);

  const [, setOpenActionMenu] = useState<string | null>(null);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<BulkImportHistoryItem | null>(null);
  const [selectedCertificationsModal, setSelectedCertificationsModal] = useState<{certifications: AssignmentCertification[], assignmentId: string} | null>(null);
  const [selectedAssignedUsersModal, setSelectedAssignedUsersModal] = useState<{
    users?: {_id: string, name: string, email: string}[],
    groups?: {group_key: string, group_name: string, user_count: number}[],
    roles?: {role_key: string, role_name: string, user_count: number}[],
    type: 'USER' | 'GROUP' | 'ROLE'
  } | null>(null);
  const [selectedSkippedUsersModal, setSelectedSkippedUsersModal] = useState<{
    skipped_users?: {user_id: string, user_email: string, user_name: string, certification_id: string, reason: string}[],
    skipped_groups?: {group_key: string, group_name: string, certification_id: string, reason: string}[],
    skipped_roles?: {group_key: string, role_key: string, role_name: string, certification_id: string, reason: string}[]
  } | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const [, setSearchParams] = useSearchParams();

  // Data states
  const [overviewMetrics, setOverviewMetrics] = useState<OverviewAnalytics | null>(null);
  const [userAssignments, setUserAssignments] = useState<AssignmentListResponse | null>(null);
  const [groupAssignments, setGroupAssignments] = useState<AssignmentListResponse | null>(null);
  const [roleAssignments, setRoleAssignments] = useState<AssignmentListResponse | null>(null);
  const [bulkImportHistory, setBulkImportHistory] = useState<BulkImportHistoryResponse | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Fetch certification dropdown data
  const { data: certificationDropdownData } = useQuery({
    queryKey: ['certification-dropdown'],
    queryFn: () => userService.getCertificationDropdown(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });

  const certificationOptions = certificationDropdownData?.data || [];

  // Debug: Log certification options to verify structure
  useEffect(() => {
    if (certificationOptions.length > 0) {
      console.log('Certification Options:', certificationOptions);
      console.log('First Certification:', certificationOptions[0]);
    }
  }, [certificationOptions]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
    if (selectedCertification && selectedCertification !== 'all') params.set('certification', selectedCertification);
    setSearchParams(params, { replace: true });
  }, [debouncedSearchTerm, selectedCertification, setSearchParams]);

  // Load initial data on mount - fetch all assignment types
  useEffect(() => {
    loadOverviewAnalytics();
    loadBulkImportHistory();
    // Load all three assignment types on initial mount
    loadUserAssignments();
    loadGroupAssignments();
    loadRoleAssignments();
  }, []);

  // Load overview analytics when granularity changes (only overview, not tabs)
  useEffect(() => {
    loadOverviewAnalytics();
  }, [selectedGranularity]);

  // Load user assignments with filters (using debounced search)
  useEffect(() => {
    if (activeTab === 'users') {
      loadUserAssignments();
    }
  }, [activeTab, currentPage, pageSize, debouncedSearchTerm, filterStatus, selectedCertification]);

  // Load group assignments with filters (using debounced search)
  useEffect(() => {
    if (activeTab === 'groups') {
      loadGroupAssignments();
    }
  }, [activeTab, currentPage, pageSize, debouncedSearchTerm, selectedCertification]);

  // Load role assignments with filters (using debounced search)
  useEffect(() => {
    if (activeTab === 'roles') {
      loadRoleAssignments();
    }
  }, [activeTab, currentPage, pageSize, debouncedSearchTerm, selectedCertification]);

  // Load bulk import history (using debounced search)
  useEffect(() => {
    if (activeTab === 'bulk-history') {
      loadBulkImportHistory();
    }
  }, [activeTab, currentPage, pageSize, debouncedSearchTerm, bulkHistoryStatusFilter]);

  // Reset to first page when debounced search changes
  useEffect(() => {
    if (debouncedSearchTerm !== '') {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadOverviewAnalytics(),
        loadUserAssignments(),
        loadGroupAssignments(),
        loadRoleAssignments(),
      ]);
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllTabData = async () => {
    try {
      // Load all tab data in parallel in the background
      await Promise.all([
        loadUserAssignments(),
        loadGroupAssignments(),
        loadRoleAssignments(),
        loadBulkImportHistory()
      ]);
    } catch (error) {
      console.error('Error loading tab analytics data:', error);
    }
  };

  const loadOverviewAnalytics = async () => {
    setOverviewLoading(true);
    try {
      const response = await certificationAssignmentService.getOverviewAnalytics(selectedGranularity);

      // Map API response to UI format
      const metrics: OverviewAnalytics = {
        totalAssigned: response.totalAssigned,
        completionRate: response.completionRate,
        avgProgress: response.avgProgress,
        avgTimeSpent: response.avgTimeSpent,
        engagementScore: response.engagementScore,
        onTimeRate: response.onTimeRate,
        periodComparison: {
          totalAssigned: {
            change: `${response.periodComparison.totalAssigned.change >= 0 ? '+' : ''}${response.periodComparison.totalAssigned.change}`
          },
          completionRate: {
            change: `${response.periodComparison.completionRate.change >= 0 ? '+' : ''}${response.periodComparison.completionRate.change}`
          }
        }
      };

      setOverviewMetrics(metrics);
    } catch (error: any) {
      console.error('Error loading overview analytics:', error);
      toast.error('Failed to load overview metrics');
    } finally {
      setOverviewLoading(false);
    }
  };

  const handleRevokeAssignment = async () => {
    if (!revokeModal) return;
    setRevokeLoading(true);
    try {
      const result = await certificationAssignmentService.revokeAssignment(revokeModal.assignmentId);
      toast.success(
        `Revoked ${revokeModal.type === 'GROUP' ? 'department' : 'role'} assignment. Deleted: ${result.certification_trackings_deleted} tracking(s), ${result.exam_attempts_deleted} attempt(s), ${result.certificates_deleted} certificate(s).`
      );
      setRevokeModal(null);
      if (activeTab === 'groups') loadGroupAssignments();
      else if (activeTab === 'roles') loadRoleAssignments();
    } catch (error: any) {
      const msg = error.response?.data?.error?.error_description || error.message || 'Failed to revoke assignment';
      toast.error(msg);
    } finally {
      setRevokeLoading(false);
    }
  };

  const handleSendReminder = async (assignmentId: string) => {
    try {
      setActionLoading(assignmentId);

      const reminderData = await certificationAssignmentService.sendReminderNotifications(assignmentId);

      const { message, failure_count } = reminderData;

      if (failure_count === 0) {
        toast.success(message);
      } else {
        toast.warning(message);
      }

    } catch (error: any) {
      console.error('Failed to send reminders:', error);
      toast.error(error.message || 'Failed to send reminders');
    } finally {
      setActionLoading(null);
    }
  };

  const loadUserAssignments = async () => {
    setUserTableLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: pageSize,
        assignment_type: 'USER'
      };

      // Add search filter if provided (using debounced search term)
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }

      // Add certification filter if provided
      if (selectedCertification && selectedCertification !== 'all') {
        params.certification_id = selectedCertification;
        console.log('USER - Selected Certification ID:', selectedCertification);
      }

      // Add status filter if provided
      if (filterStatus && filterStatus !== 'all') {
        params.status = filterStatus;
      }

      console.log('USER - API Params being sent:', params);
      const response = await certificationAssignmentService.listAssignments(params);

      console.log('User Assignments API Response:', response);

      // Check if response has the expected structure
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response structure');
      }

      // Service already returns response.data.data, so response itself contains { data: [...], total, page, limit, summary }
      const responseData = response.data || [];
      const total = response.total || 0;
      const page = response.page || currentPage;
      const limit = response.limit || pageSize;
      const summary = response.summary || {
        total_users_assigned: 0,
        total_groups_assigned: 0,
        total_roles_assigned: 0
      };

      console.log('User assignments parsed:', { total, page, limit, dataLength: responseData.length });

      const formattedData: AssignmentListResponse = {
        data: responseData.map((item: any) => ({
          ...item,
          // Ensure assigned_to_details exists with proper structure
          assigned_to_details: item.assigned_to_details || {
            users: item.assigned_to?.users || [],
            groups: item.assigned_to?.groups || [],
            roles: item.assigned_to?.roles || []
          },
          // Ensure assigned_by_details exists
          assigned_by_details: item.assigned_by_details || item.assigned_by || {
            name: 'Unknown',
            email: 'N/A'
          },
          // Ensure certifications array exists
          certifications: item.certifications || []
        })),
        total: total,
        page: page,
        limit: limit,
        summary: summary
      };

      console.log('Formatted user assignments data:', formattedData);
      setUserAssignments(formattedData);
    } catch (error: any) {
      console.error('Error loading user assignments:', error);
      console.error('Error details:', error?.response?.data || error.message);
      toast.error('Failed to load user assignments');
      setUserAssignments({
        data: [],
        total: 0,
        page: 1,
        limit: pageSize,
        summary: { total_users_assigned: 0, total_groups_assigned: 0, total_roles_assigned: 0 }
      });
    } finally {
      setUserTableLoading(false);
    }
  };

  const loadGroupAssignments = async () => {
    setGroupTableLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: pageSize,
        assignment_type: 'GROUP'
      };

      // Add search filter if provided (using debounced search term)
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }

      // Add certification filter if provided
      if (selectedCertification && selectedCertification !== 'all') {
        params.certification_id = selectedCertification;
        console.log('GROUP - Selected Certification ID:', selectedCertification);
      }

      console.log('GROUP - API Params being sent:', params);
      const response = await certificationAssignmentService.listAssignments(params);

      console.log('Group Assignments API Response:', response);

      // Check if response has the expected structure
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response structure');
      }

      // Service already returns response.data.data, so response itself contains { data: [...], total, page, limit, summary }
      const responseData = response.data || [];
      const total = response.total || 0;
      const page = response.page || currentPage;
      const limit = response.limit || pageSize;
      const summary = response.summary || {
        total_users_assigned: 0,
        total_groups_assigned: 0,
        total_roles_assigned: 0
      };

      console.log('Group assignments parsed:', { total, page, limit, dataLength: responseData.length });

      const formattedData: AssignmentListResponse = {
        data: responseData.map((item: any) => ({
          ...item,
          // Ensure assigned_to_details exists with proper structure
          assigned_to_details: item.assigned_to_details || {
            users: item.assigned_to?.users || [],
            groups: item.assigned_to?.groups || [],
            roles: item.assigned_to?.roles || []
          },
          // Ensure assigned_by_details exists
          assigned_by_details: item.assigned_by_details || item.assigned_by || {
            name: 'Unknown',
            email: 'N/A'
          },
          // Ensure certifications array exists
          certifications: item.certifications || []
        })),
        total: total,
        page: page,
        limit: limit,
        summary: summary
      };

      setGroupAssignments(formattedData);
    } catch (error: any) {
      console.error('Error loading group assignments:', error);
      console.error('Error details:', error?.response?.data || error.message);
      toast.error('Failed to load group assignments');
      setGroupAssignments({
        data: [],
        total: 0,
        page: 1,
        limit: pageSize,
        summary: { total_users_assigned: 0, total_groups_assigned: 0, total_roles_assigned: 0 }
      });
    } finally {
      setGroupTableLoading(false);
    }
  };

  const loadRoleAssignments = async () => {
    setRoleTableLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: pageSize,
        assignment_type: 'ROLE'
      };

      // Add search filter if provided (using debounced search term)
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }

      // Add certification filter if provided
      if (selectedCertification && selectedCertification !== 'all') {
        params.certification_id = selectedCertification;
        console.log('ROLE - Selected Certification ID:', selectedCertification);
      }

      console.log('ROLE - API Params being sent:', params);
      const response = await certificationAssignmentService.listAssignments(params);

      console.log('Role Assignments API Response:', response);

      // Check if response has the expected structure
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response structure');
      }

      // Service already returns response.data.data, so response itself contains { data: [...], total, page, limit, summary }
      const responseData = response.data || [];
      const total = response.total || 0;
      const page = response.page || currentPage;
      const limit = response.limit || pageSize;
      const summary = response.summary || {
        total_users_assigned: 0,
        total_groups_assigned: 0,
        total_roles_assigned: 0
      };

      console.log('Role assignments parsed:', { total, page, limit, dataLength: responseData.length });

      const formattedData: AssignmentListResponse = {
        data: responseData.map((item: any) => ({
          ...item,
          // Ensure assigned_to_details exists with proper structure
          assigned_to_details: item.assigned_to_details || {
            users: item.assigned_to?.users || [],
            groups: item.assigned_to?.groups || [],
            roles: item.assigned_to?.roles || []
          },
          // Ensure assigned_by_details exists
          assigned_by_details: item.assigned_by_details || item.assigned_by || {
            name: 'Unknown',
            email: 'N/A'
          },
          // Ensure certifications array exists
          certifications: item.certifications || []
        })),
        total: total,
        page: page,
        limit: limit,
        summary: summary
      };

      setRoleAssignments(formattedData);
    } catch (error: any) {
      console.error('Error loading role assignments:', error);
      console.error('Error details:', error?.response?.data || error.message);
      toast.error('Failed to load role assignments');
      setRoleAssignments({
        data: [],
        total: 0,
        page: 1,
        limit: pageSize,
        summary: { total_users_assigned: 0, total_groups_assigned: 0, total_roles_assigned: 0 }
      });
    } finally {
      setRoleTableLoading(false);
    }
  };

  const loadBulkImportHistory = async () => {
    setBulkHistoryLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: pageSize
      };

      // Add search filter if provided (search by identifier or certification slug, using debounced search term)
      if (debouncedSearchTerm) {
        params.search_identifier = debouncedSearchTerm;
      }

      // Add status filter if provided
      if (bulkHistoryStatusFilter && bulkHistoryStatusFilter !== 'all') {
        params.status = bulkHistoryStatusFilter;
      }

      const response = await certificationAssignmentService.getBulkImportHistory(params);

      console.log('Bulk Import History API Response:', response);

      // Check if response has the expected structure
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response structure');
      }

      // Service returns { imports: [...], pagination: {...} }
      const importsData = response.imports || [];
      const paginationData = response.pagination || { total: 0, total_pages: 0 };

      // Map API response to UI format
      const formattedData: BulkImportHistoryResponse = {
        data: importsData.map((item: any) => ({
          _id: item._id || item.import_id,
          import_date: item.import_date,
          imported_by: item.imported_by,
          status: item.status,
          total_records: item.total_records || 0,
          success_count: item.success_count || 0,
          failed_count: item.failed_count || 0,
          results: item.results || []
        })),
        pagination: {
          total: paginationData.total || 0,
          total_pages: paginationData.total_pages || 0
        }
      };

      setBulkImportHistory(formattedData);
    } catch (error: any) {
      console.error('Error loading bulk import history:', error);
      console.error('Error details:', error?.response?.data || error.message);
      toast.error('Failed to load bulk import history');
      setBulkImportHistory({ data: [], pagination: { total: 0, total_pages: 0 } });
    } finally {
      setBulkHistoryLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      loadAllData(),
      loadAllTabData()
    ]);
    setRefreshing(false);
    toast.success('Data refreshed successfully');
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="space-y-6">
      {/* Gradient Header with Metrics */}
      <div className="bg-gradient-to-r from-brand-amber-600 to-brand-orange-600 rounded-xl p-4 md:p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 md:mb-6 gap-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold mb-2 flex items-center gap-2">
              <Award className="w-6 h-6 md:w-7 md:h-7" />
              Certification Assignment Analytics
            </h1>
            <p className="text-brand-amber-100 text-sm md:text-base">
              Track and analyze certification assignments across users, groups, and roles
            </p>
          </div>
          <div className="flex gap-2 md:gap-3 flex-wrap">
            <div className="min-w-[140px]">
              <Select
                control={control}
                name="selectedGranularity"
                options={[
                  { value: 'weekly', label: 'Weekly' },
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'quarterly', label: 'Quarterly' },
                  { value: 'yearly', label: 'Yearly' }
                ]}
                placeholder="Select Period"
                isClearable={false}
                isSearchable={false}
              />
            </div>
            <button
              onClick={() => window.location.href = '/admin/certification/assignment'}
              disabled={!canAssign}
              title={!canAssign ? 'You do not have permission to perform this action' : undefined}
              className={`flex items-center gap-2 px-3 md:px-4 py-2 bg-white text-brand-amber-600 rounded-lg font-medium text-sm md:text-base transition-colors ${canAssign ? 'hover:bg-brand-amber-50' : 'opacity-50 cursor-not-allowed'}`}
            >
              <Users className="w-4 h-4 md:w-5 md:h-5" />
              <span className="hidden sm:inline">Assign Certification</span>
              <span className="sm:hidden">Assign</span>
            </button>
          </div>
        </div>

        {/* Key Metrics in Header */}
        {overviewLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-lg p-3 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="h-3 bg-white/20 rounded w-20 mb-2"></div>
                    <div className="h-8 bg-white/20 rounded w-16 mb-2"></div>
                    <div className="h-3 bg-white/20 rounded w-24"></div>
                  </div>
                  <div className="w-8 h-8 bg-white/20 rounded-full"></div>
                </div>
              </div>
            ))}
          </div>
        ) : overviewMetrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-amber-100 text-xs">Total Assigned</p>
                  <p className="text-2xl font-bold">{overviewMetrics.totalAssigned}</p>
                  <p className="text-brand-amber-200 text-xs mt-1">
                    <span className={overviewMetrics.periodComparison.totalAssigned.change.startsWith('+') ? 'text-green-300' : 'text-red-300'}>
                      {overviewMetrics.periodComparison.totalAssigned.change}
                    </span> vs last period
                  </p>
                </div>
                <Award className="w-8 h-8 text-brand-amber-200" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-amber-100 text-xs">Completion Rate</p>
                  <p className="text-2xl font-bold">{overviewMetrics.completionRate}%</p>
                  <p className="text-brand-amber-200 text-xs mt-1">
                    <span className={overviewMetrics.periodComparison.completionRate.change.startsWith('+') ? 'text-green-300' : 'text-red-300'}>
                      {overviewMetrics.periodComparison.completionRate.change}
                    </span> improvement
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-brand-amber-200" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-amber-100 text-xs">Time Spent </p>
                  <p className="text-2xl font-bold">{formatTime(overviewMetrics.avgTimeSpent)}</p>
                  <p className="text-brand-amber-200 text-xs mt-1">
                     avg time
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-brand-amber-200" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-amber-100 text-xs">Engagement Score</p>
                  <p className="text-2xl font-bold">{overviewMetrics.engagementScore}%</p>
                  <p className="text-brand-amber-200 text-xs mt-1">
                    <span className="text-yellow-300">{overviewMetrics.onTimeRate}%</span> on-time
                  </p>
                </div>
                <Activity className="w-8 h-8 text-brand-amber-200" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max md:min-w-0">
            {[
              { key: 'users', label: 'User Assignments', icon: User, count: userAssignments?.total || 0 },
              { key: 'groups', label: 'Department Assignments', icon: Users, count: groupAssignments?.total || 0 },
              { key: 'roles', label: 'Role Assignments', icon: Shield, count: roleAssignments?.total || 0 },
              { key: 'bulk-history', label: 'Bulk Import History', icon: History, count: bulkImportHistory?.pagination.total || 0 }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key as any);
                  setCurrentPage(1);
                  setSearchTerm(''); // Reset search when switching tabs
                }}
                className={`flex items-center gap-1 md:gap-2 px-3 md:px-6 py-3 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-brand-amber-600 text-brand-amber-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                <span className="px-1.5 md:px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* Search Input */}
            <div className="flex-1 sm:flex-[2] md:flex-[2] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                placeholder={
                  activeTab === 'users' ? 'Search by user name email or employee ID...' :
                  activeTab === 'groups' ? 'Search by group name...' :
                  activeTab === 'roles' ? 'Search by role name...' :
                  'Search by email...'
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 text-sm sm:text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-amber-500"
              />
            </div>

            {/* Certification Dropdown - Only for users, groups, roles tabs */}
            {(activeTab === 'users' || activeTab === 'groups' || activeTab === 'roles') && (
              <div className="flex-1 sm:flex-[1.5] md:flex-[1.5] relative">
                <Select
                  control={control}
                  name="selectedCertification"
                  options={[
                    { value: 'all', label: 'All Certifications' },
                    ...certificationOptions.map((certification: any) => ({
                      value: certification.certification_id || certification._id || certification.id,
                      label: certification.title || certification.name || 'Unknown'
                    }))
                  ]}
                  placeholder="Select Certification"
                  isClearable={false}
                  isSearchable={true}
                />
              </div>
            )}

            {/* Bulk History Status Filter */}
            {activeTab === 'bulk-history' && (
              <div className="flex-1 sm:flex-[1] md:flex-[1]">
                <Select
                  control={control}
                  name="bulkHistoryStatusFilter"
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'COMPLETED', label: 'Completed' },
                    { value: 'PROCESSING', label: 'Processing' },
                    { value: 'FAILED', label: 'Failed' }
                  ]}
                  placeholder="Filter by Status"
                  isClearable={false}
                  isSearchable={false}
                />
              </div>
            )}

            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`flex-shrink-0 p-2 text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed self-start sm:self-auto ${
                refreshing ? 'animate-spin' : ''
              }`}
              title="Refresh data"
            >
              <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'users' && (
          <div className="p-6">
            {/* User Assignments Table */}
            {userTableLoading ? (
              /* Skeleton Loader */
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      </div>
                      <div className="h-6 w-24 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : userAssignments && userAssignments.data.length > 0 ? (
              <>
                <div className="overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden inline-block min-w-full align-middle">
                    <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Users</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Certifications</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skipped Users</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mandatory</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned By</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deadline</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {userAssignments.data.map((assignment) => (
                        <tr key={assignment._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-2">
                              {assignment.assigned_to_details.users.slice(0, 1).map((user) => (
                                <div key={user._id} className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                    {user.name.split(' ').map((n: string) => n[0]).join('')}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                                    <p className="text-xs text-gray-500">{user.email}</p>
                                  </div>
                                </div>
                              ))}
                              {assignment.assigned_to_details.users.length > 1 && (
                                <button
                                  onClick={() => setSelectedAssignedUsersModal({
                                    users: assignment.assigned_to_details.users,
                                    type: 'USER'
                                  })}
                                  className="text-xs text-brand-amber-600 font-medium hover:text-brand-amber-800 hover:underline ml-10"
                                >
                                  +{assignment.assigned_to_details.users.length - 1} more users
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="text-sm text-gray-900 truncate max-w-[200px]">
                                {assignment.certifications[0]?.name || 'N/A'}
                              </div>
                              {assignment.certifications.length > 1 && (
                                <button
                                  onClick={() => setSelectedCertificationsModal({certifications: assignment.certifications, assignmentId: assignment._id})}
                                  className="text-xs text-brand-amber-600 font-medium hover:text-brand-amber-800 hover:underline"
                                >
                                  +{assignment.certifications.length - 1} more
                                </button>
                              )}
                              {assignment.certifications.length === 1 && (
                                <button
                                  onClick={() => setSelectedCertificationsModal({certifications: assignment.certifications, assignmentId: assignment._id})}
                                  className="p-1 text-gray-400 hover:text-brand-amber-600 transition-colors"
                                  title="View certification details"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                          {/* Skipped Users Cell */}
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-2">
                              {((assignment as any).skipped_users?.length > 0 || (assignment as any).skipped_groups?.length > 0 || (assignment as any).skipped_roles?.length > 0) ? (
                                <>
                                  {/* Display first skipped user */}
                                  {(assignment as any).skipped_users?.slice(0, 1).map((user: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        {user.user_name ? user.user_name.split(' ').map((n: string) => n[0]).join('') : '?'}
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{user.user_name || 'Unknown User'}</p>
                                        <p className="text-xs text-gray-500">{user.user_email || 'N/A'}</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Display first skipped group if no users */}
                                  {(assignment as any).skipped_users?.length === 0 && (assignment as any).skipped_groups?.slice(0, 1).map((group: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-purple-500 to-brand-pink-500 rounded-full flex items-center justify-center text-white">
                                        <Users className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{group.group_name || 'Unknown Group'}</p>
                                        <p className="text-xs text-gray-500">Group</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Display first skipped role if no users and no groups */}
                                  {(assignment as any).skipped_users?.length === 0 && (assignment as any).skipped_groups?.length === 0 && (assignment as any).skipped_roles?.slice(0, 1).map((role: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-cyan-500 rounded-full flex items-center justify-center text-white">
                                        <Shield className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{role.role_name || 'Unknown Role'}</p>
                                        <p className="text-xs text-gray-500">Role</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Show more button */}
                                  {(() => {
                                    const total = ((assignment as any).skipped_users?.length || 0) + ((assignment as any).skipped_groups?.length || 0) + ((assignment as any).skipped_roles?.length || 0);
                                    return total > 1 ? (
                                      <button
                                        onClick={() => setSelectedSkippedUsersModal({
                                          skipped_users: (assignment as any).skipped_users,
                                          skipped_groups: (assignment as any).skipped_groups,
                                          skipped_roles: (assignment as any).skipped_roles
                                        })}
                                        className="text-xs text-brand-amber-600 font-medium hover:text-brand-amber-800 hover:underline ml-10"
                                      >
                                        +{total - 1} more skipped
                                      </button>
                                    ) : null;
                                  })()}
                                </>
                              ) : (
                                <span className="text-sm text-gray-400">None</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              assignment.is_mandatory ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`}>
                              {assignment.is_mandatory ? 'True' : 'False'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{assignment.assigned_by_details.name}</div>
                            <div className="text-xs text-gray-500">{assignment.assigned_by_details.email}</div>
                          </td>
                          <td className="px-6 py-4">
                            {assignment.deadline_days ? (
                              <div className="text-sm text-gray-900">
                                {assignment.deadline_days} {assignment.deadline_days === 1 ? 'day' : 'days'}
                              </div>
                            ) : assignment.deadline_date ? (
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-900">
                                  {new Date(assignment.deadline_date).toLocaleDateString()}
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">No deadline</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              assignment.status === 'ACTIVE' ? 'bg-green-50 text-green-700' :
                              assignment.status === 'COMPLETED' ? 'bg-blue-50 text-blue-700' :
                              assignment.status === 'CANCELLED' ? 'bg-red-50 text-red-700' :
                              'bg-gray-50 text-gray-700'
                            }`}>
                              {assignment.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleSendReminder(assignment._id)}
                              disabled={!canWrite || actionLoading === assignment._id}
                              title={!canWrite ? 'You do not have permission to perform this action' : undefined}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {actionLoading === assignment._id ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
                                  <span>Sending...</span>
                                </>
                              ) : (
                                <>
                                  <Mail className="w-4 h-4" />
                                  <span>Reminder</span>
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>

                {/* Pagination */}
                {userAssignments && userAssignments.total > 0 && (
                  <div className="mt-4">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(userAssignments.total / pageSize)}
                      totalItems={userAssignments.total}
                      itemsPerPage={pageSize}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={setPageSize}
                      showItemsPerPage={true}
                      itemsPerPageOptions={[10, 20, 50, 100]}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <User className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-900 font-medium mb-1">No user assignments found</p>
                  <p className="text-sm text-gray-500">Assign certifications to users to see them here</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'groups' && (
          <div className="p-6">
            {/* Group Assignment Table */}
            {groupTableLoading ? (
              /* Skeleton Loader */
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      </div>
                      <div className="h-6 w-24 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : groupAssignments && groupAssignments.data.length > 0 ? (
              <>
                <div className="overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden inline-block min-w-full align-middle">
                    <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Groups</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Certifications</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skipped Users</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mandatory</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned By</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deadline</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {groupAssignments.data.map((assignment) => (
                        <tr key={assignment._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-2">
                              {assignment.assigned_to_details.groups.slice(0, 1).map((group) => (
                                <div key={group.group_key} className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-gradient-to-r from-brand-purple-500 to-brand-pink-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                    <Users className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{group.group_name}</p>
                                    <p className="text-xs text-gray-500">{group.user_count} members</p>
                                  </div>
                                </div>
                              ))}
                              {assignment.assigned_to_details.groups.length > 1 && (
                                <button
                                  onClick={() => setSelectedAssignedUsersModal({
                                    groups: assignment.assigned_to_details.groups,
                                    type: 'GROUP'
                                  })}
                                  className="text-xs text-brand-amber-600 font-medium hover:text-brand-amber-800 hover:underline ml-10"
                                >
                                  +{assignment.assigned_to_details.groups.length - 1} more groups
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="text-sm text-gray-900 truncate max-w-[200px]">
                                {assignment.certifications[0]?.name || 'N/A'}
                              </div>
                              {assignment.certifications.length > 1 && (
                                <button
                                  onClick={() => setSelectedCertificationsModal({certifications: assignment.certifications, assignmentId: assignment._id})}
                                  className="text-xs text-brand-amber-600 font-medium hover:text-brand-amber-800 hover:underline"
                                >
                                  +{assignment.certifications.length - 1} more
                                </button>
                              )}
                              {assignment.certifications.length === 1 && (
                                <button
                                  onClick={() => setSelectedCertificationsModal({certifications: assignment.certifications, assignmentId: assignment._id})}
                                  className="p-1 text-gray-400 hover:text-brand-amber-600 transition-colors"
                                  title="View certification details"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                          {/* Skipped Users Cell */}
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-2">
                              {((assignment as any).skipped_users?.length > 0 || (assignment as any).skipped_groups?.length > 0 || (assignment as any).skipped_roles?.length > 0) ? (
                                <>
                                  {/* Display first skipped user */}
                                  {(assignment as any).skipped_users?.slice(0, 1).map((user: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        {user.user_name ? user.user_name.split(' ').map((n: string) => n[0]).join('') : '?'}
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{user.user_name || 'Unknown User'}</p>
                                        <p className="text-xs text-gray-500">{user.user_email || 'N/A'}</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Display first skipped group if no users */}
                                  {(assignment as any).skipped_users?.length === 0 && (assignment as any).skipped_groups?.slice(0, 1).map((group: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-purple-500 to-brand-pink-500 rounded-full flex items-center justify-center text-white">
                                        <Users className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{group.group_name || 'Unknown Group'}</p>
                                        <p className="text-xs text-gray-500">Group</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Display first skipped role if no users and no groups */}
                                  {(assignment as any).skipped_users?.length === 0 && (assignment as any).skipped_groups?.length === 0 && (assignment as any).skipped_roles?.slice(0, 1).map((role: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-cyan-500 rounded-full flex items-center justify-center text-white">
                                        <Shield className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{role.role_name || 'Unknown Role'}</p>
                                        <p className="text-xs text-gray-500">Role</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Show more button */}
                                  {(() => {
                                    const total = ((assignment as any).skipped_users?.length || 0) + ((assignment as any).skipped_groups?.length || 0) + ((assignment as any).skipped_roles?.length || 0);
                                    return total > 1 ? (
                                      <button
                                        onClick={() => setSelectedSkippedUsersModal({
                                          skipped_users: (assignment as any).skipped_users,
                                          skipped_groups: (assignment as any).skipped_groups,
                                          skipped_roles: (assignment as any).skipped_roles
                                        })}
                                        className="text-xs text-brand-amber-600 font-medium hover:text-brand-amber-800 hover:underline ml-10"
                                      >
                                        +{total - 1} more skipped
                                      </button>
                                    ) : null;
                                  })()}
                                </>
                              ) : (
                                <span className="text-sm text-gray-400">None</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              assignment.is_mandatory ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-700'
                            }`}>
                              {assignment.is_mandatory ? 'True' : 'False'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{assignment.assigned_by_details.name}</div>
                            <div className="text-xs text-gray-500">{assignment.assigned_by_details.email}</div>
                          </td>
                          <td className="px-6 py-4">
                            {assignment.deadline_days ? (
                              <div className="text-sm text-gray-900">
                                {assignment.deadline_days} {assignment.deadline_days === 1 ? 'day' : 'days'}
                              </div>
                            ) : assignment.deadline_date ? (
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-900">
                                  {new Date(assignment.deadline_date).toLocaleDateString()}
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">No deadline</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              assignment.status === 'ACTIVE' ? 'bg-green-50 text-green-700' :
                              assignment.status === 'COMPLETED' ? 'bg-blue-50 text-blue-700' :
                              assignment.status === 'CANCELLED' ? 'bg-red-50 text-red-700' :
                              'bg-gray-50 text-gray-700'
                            }`}>
                              {assignment.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleSendReminder(assignment._id)}
                                disabled={!canWrite || actionLoading === assignment._id}
                                title={!canWrite ? 'You do not have permission to perform this action' : undefined}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {actionLoading === assignment._id ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
                                    <span>Sending...</span>
                                  </>
                                ) : (
                                  <>
                                    <Mail className="w-4 h-4" />
                                    <span>Reminder</span>
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => setRevokeModal({
                                  assignmentId: assignment._id,
                                  type: 'GROUP',
                                  name: assignment.assigned_to_details?.groups?.[0]?.group_name || 'this department'
                                })}
                                disabled={!canWrite}
                                title={!canWrite ? 'You do not have permission to perform this action' : 'Revoke assignment'}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Trash2 className="w-4 h-4" />
                                <span>Revoke</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>

                {/* Pagination */}
                {groupAssignments && groupAssignments.total > 0 && (
                  <div className="mt-4">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(groupAssignments.total / pageSize)}
                      totalItems={groupAssignments.total}
                      itemsPerPage={pageSize}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={setPageSize}
                      showItemsPerPage={true}
                      itemsPerPageOptions={[10, 20, 50, 100]}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Users className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-900 font-medium mb-1">No group assignments found</p>
                  <p className="text-sm text-gray-500">Assign certifications to groups to see them here</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'roles' && (
          <div className="p-6">
            {/* Role Assignment Table */}
            {roleTableLoading ? (
              /* Skeleton Loader */
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      </div>
                      <div className="h-6 w-24 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : roleAssignments && roleAssignments.data.length > 0 ? (
              <>
                <div className="overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden inline-block min-w-full align-middle">
                    <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Roles</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Certifications</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skipped Users</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mandatory</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned By</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deadline</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {roleAssignments.data.map((assignment) => (
                        <tr key={assignment._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-2">
                              {assignment.assigned_to_details.roles.slice(0, 1).map((role) => (
                                <div key={role.role_key} className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                    <Shield className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{role.role_name}</p>
                                    <p className="text-xs text-gray-500">{role.user_count} users</p>
                                  </div>
                                </div>
                              ))}
                              {assignment.assigned_to_details.roles.length > 1 && (
                                <button
                                  onClick={() => setSelectedAssignedUsersModal({
                                    roles: assignment.assigned_to_details.roles,
                                    type: 'ROLE'
                                  })}
                                  className="text-xs text-brand-amber-600 font-medium hover:text-brand-amber-800 hover:underline ml-10"
                                >
                                  +{assignment.assigned_to_details.roles.length - 1} more roles
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="text-sm text-gray-900 truncate max-w-[200px]">
                                {assignment.certifications[0]?.name || 'N/A'}
                              </div>
                              {assignment.certifications.length > 1 && (
                                <button
                                  onClick={() => setSelectedCertificationsModal({certifications: assignment.certifications, assignmentId: assignment._id})}
                                  className="text-xs text-brand-amber-600 font-medium hover:text-brand-amber-800 hover:underline"
                                >
                                  +{assignment.certifications.length - 1} more
                                </button>
                              )}
                              {assignment.certifications.length === 1 && (
                                <button
                                  onClick={() => setSelectedCertificationsModal({certifications: assignment.certifications, assignmentId: assignment._id})}
                                  className="p-1 text-gray-400 hover:text-brand-amber-600 transition-colors"
                                  title="View certification details"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                          {/* Skipped Users Cell */}
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-2">
                              {((assignment as any).skipped_users?.length > 0 || (assignment as any).skipped_groups?.length > 0 || (assignment as any).skipped_roles?.length > 0) ? (
                                <>
                                  {/* Display first skipped user */}
                                  {(assignment as any).skipped_users?.slice(0, 1).map((user: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        {user.user_name ? user.user_name.split(' ').map((n: string) => n[0]).join('') : '?'}
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{user.user_name || 'Unknown User'}</p>
                                        <p className="text-xs text-gray-500">{user.user_email || 'N/A'}</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Display first skipped group if no users */}
                                  {(assignment as any).skipped_users?.length === 0 && (assignment as any).skipped_groups?.slice(0, 1).map((group: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-purple-500 to-brand-pink-500 rounded-full flex items-center justify-center text-white">
                                        <Users className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{group.group_name || 'Unknown Group'}</p>
                                        <p className="text-xs text-gray-500">Group</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Display first skipped role if no users and no groups */}
                                  {(assignment as any).skipped_users?.length === 0 && (assignment as any).skipped_groups?.length === 0 && (assignment as any).skipped_roles?.slice(0, 1).map((role: any, index: number) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-r from-brand-blue-500 to-brand-cyan-500 rounded-full flex items-center justify-center text-white">
                                        <Shield className="w-4 h-4" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{role.role_name || 'Unknown Role'}</p>
                                        <p className="text-xs text-gray-500">Role</p>
                                      </div>
                                    </div>
                                  ))}
                                  {/* Show more button */}
                                  {(() => {
                                    const total = ((assignment as any).skipped_users?.length || 0) + ((assignment as any).skipped_groups?.length || 0) + ((assignment as any).skipped_roles?.length || 0);
                                    return total > 1 ? (
                                      <button
                                        onClick={() => setSelectedSkippedUsersModal({
                                          skipped_users: (assignment as any).skipped_users,
                                          skipped_groups: (assignment as any).skipped_groups,
                                          skipped_roles: (assignment as any).skipped_roles
                                        })}
                                        className="text-xs text-brand-amber-600 font-medium hover:text-brand-amber-800 hover:underline ml-10"
                                      >
                                        +{total - 1} more skipped
                                      </button>
                                    ) : null;
                                  })()}
                                </>
                              ) : (
                                <span className="text-sm text-gray-400">None</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              assignment.is_mandatory ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-700'
                            }`}>
                              {assignment.is_mandatory ? 'True' : 'False'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{assignment.assigned_by_details.name}</div>
                            <div className="text-xs text-gray-500">{assignment.assigned_by_details.email}</div>
                          </td>
                          <td className="px-6 py-4">
                            {assignment.deadline_days ? (
                              <div className="text-sm text-gray-900">
                                {assignment.deadline_days} {assignment.deadline_days === 1 ? 'day' : 'days'}
                              </div>
                            ) : assignment.deadline_date ? (
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-900">
                                  {new Date(assignment.deadline_date).toLocaleDateString()}
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">No deadline</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              assignment.status === 'ACTIVE' ? 'bg-green-50 text-green-700' :
                              assignment.status === 'COMPLETED' ? 'bg-blue-50 text-blue-700' :
                              assignment.status === 'CANCELLED' ? 'bg-red-50 text-red-700' :
                              'bg-gray-50 text-gray-700'
                            }`}>
                              {assignment.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleSendReminder(assignment._id)}
                                disabled={!canWrite || actionLoading === assignment._id}
                                title={!canWrite ? 'You do not have permission to perform this action' : undefined}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {actionLoading === assignment._id ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
                                    <span>Sending...</span>
                                  </>
                                ) : (
                                  <>
                                    <Mail className="w-4 h-4" />
                                    <span>Reminder</span>
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => setRevokeModal({
                                  assignmentId: assignment._id,
                                  type: 'ROLE',
                                  name: assignment.assigned_to_details?.roles?.[0]?.role_name || 'this role'
                                })}
                                disabled={!canWrite}
                                title={!canWrite ? 'You do not have permission to perform this action' : 'Revoke assignment'}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Trash2 className="w-4 h-4" />
                                <span>Revoke</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>

                {/* Pagination */}
                {roleAssignments && roleAssignments.total > 0 && (
                  <div className="mt-4">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(roleAssignments.total / pageSize)}
                      totalItems={roleAssignments.total}
                      itemsPerPage={pageSize}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={setPageSize}
                      showItemsPerPage={true}
                      itemsPerPageOptions={[10, 20, 50, 100]}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Shield className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-900 font-medium mb-1">No role assignments found</p>
                  <p className="text-sm text-gray-500">Assign certifications to roles to see them here</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bulk Import History Tab */}
        {activeTab === 'bulk-history' && (
          <div className="p-6">
            {bulkHistoryLoading ? (
              /* Skeleton Loader */
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 space-y-3">
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      </div>
                      <div className="h-6 w-24 bg-gray-200 rounded-full"></div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
                      <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-5 bg-gray-200 rounded w-1/2"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-5 bg-gray-200 rounded w-1/2"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-5 bg-gray-200 rounded w-1/2"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-5 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                    <div className="h-10 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            ) : bulkImportHistory && bulkImportHistory.data && bulkImportHistory.data.length > 0 ? (
              <>
                <div className="space-y-4 mb-6">
                  {bulkImportHistory.data.map((item) => {
                    const statusBgClass =
                      item.status === 'COMPLETED' ? 'bg-green-50' :
                      item.status === 'PROCESSING' ? 'bg-blue-50' : 'bg-red-50';

                    const statusIconClass =
                      item.status === 'COMPLETED' ? 'text-green-600' :
                      item.status === 'PROCESSING' ? 'text-blue-600' : 'text-red-600';

                    const statusBadgeClass =
                      item.status === 'COMPLETED' ? 'bg-green-50 text-green-700' :
                      item.status === 'PROCESSING' ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700';

                    const successRate = item.total_records > 0
                      ? ((item.success_count / item.total_records) * 100).toFixed(1)
                      : '0';

                    return (
                      <div key={item._id} className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                        <div className="p-4 md:p-6">
                          {/* Header */}
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className={`p-2 rounded-lg ${statusBgClass}`}>
                                  <Package className={`w-5 h-5 ${statusIconClass}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
                                    <User className="w-4 h-4 flex-shrink-0" />
                                    <span className="truncate">{item.imported_by}</span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 flex-wrap">
                                    <Calendar className="w-4 h-4 flex-shrink-0" />
                                    <span className="truncate">{new Date(item.import_date).toLocaleString()}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusBadgeClass}`}>
                              {item.status}
                            </span>
                          </div>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
                            <div className="bg-gray-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <List className="w-4 h-4 text-gray-400" />
                                <span className="text-xs text-gray-500">Total Records</span>
                              </div>
                              <p className="text-xl font-bold text-gray-900">{item.total_records}</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <span className="text-xs text-green-600">Success</span>
                              </div>
                              <p className="text-xl font-bold text-green-700">{item.success_count}</p>
                            </div>
                            <div className="bg-red-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <XCircle className="w-4 h-4 text-red-600" />
                                <span className="text-xs text-red-600">Failed</span>
                              </div>
                              <p className="text-xl font-bold text-red-700">{item.failed_count}</p>
                            </div>
                            <div className="bg-brand-amber-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <TrendingUp className="w-4 h-4 text-brand-amber-600" />
                                <span className="text-xs text-brand-amber-600">Success Rate</span>
                              </div>
                              <p className="text-xl font-bold text-brand-amber-700">{successRate}%</p>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700">Import Progress</span>
                              <span className="text-sm text-gray-500">
                                {item.success_count} / {item.total_records}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${successRate}%` }}
                              />
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => setSelectedHistoryItem(item)}
                              className="flex items-center justify-center gap-2 px-3 md:px-4 py-2 bg-brand-amber-600 text-white rounded-lg hover:bg-brand-amber-700 transition-colors text-sm font-medium w-full sm:w-auto"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View Details</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {bulkImportHistory.pagination.total > 0 && (
                  <div className="mt-6">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={bulkImportHistory.pagination.total_pages}
                      totalItems={bulkImportHistory.pagination.total}
                      itemsPerPage={pageSize}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={setPageSize}
                      showItemsPerPage={true}
                      itemsPerPageOptions={[10, 20, 50]}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <History className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Import History</h3>
                  <p className="text-gray-500 mb-4">You haven't performed any bulk imports yet</p>
                </div>
              </div>
            )}

            {/* Detail Modal */}
            <Modal
              isOpen={!!selectedHistoryItem}
              onClose={() => setSelectedHistoryItem(null)}
              title="Import Details"
              size="xl"
              maxHeight="90vh"
              footer={
                <div className="flex items-center justify-end">
                  <button
                    onClick={() => setSelectedHistoryItem(null)}
                    className="px-4 py-2 bg-brand-amber-600 text-white rounded-lg hover:bg-brand-amber-700"
                  >
                    Close
                  </button>
                </div>
              }
            >
              {selectedHistoryItem && (
                <>
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Import Date</label>
                      <p className="text-gray-900 mt-1">{new Date(selectedHistoryItem.import_date).toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Imported By</label>
                      <p className="text-gray-900 mt-1">{selectedHistoryItem.imported_by}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Status</label>
                      <p className="text-gray-900 mt-1">{selectedHistoryItem.status}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Total Records</label>
                      <p className="text-gray-900 mt-1">{selectedHistoryItem.total_records}</p>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Results</h3>
                    <div className="space-y-2">
                      {selectedHistoryItem.results && selectedHistoryItem.results.length > 0 ? selectedHistoryItem.results.map((result, idx) => (
                        <div
                          key={idx}
                          className={`p-4 rounded-lg border ${
                            result.status === 'success'
                              ? 'bg-green-50 border-green-200'
                              : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-medium text-gray-700">Row {result.row_number}</span>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
                                  {result.type}
                                </span>
                              </div>
                              <p className="text-sm text-gray-900 mb-1">
                                <span className="font-medium">Identifier:</span> {result.identifier}
                              </p>
                              <p className="text-sm text-gray-900 mb-1">
                                <span className="font-medium">Certification:</span> {result.certification_slug}
                              </p>
                              {result.error && (
                                <p className="text-sm text-red-700 mt-2">
                                  <span className="font-medium">Error:</span> {result.error}
                                </p>
                              )}
                            </div>
                            {result.status === 'success' ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-600" />
                            )}
                          </div>
                        </div>
                      )) : (
                        <div className="text-center py-8 text-gray-500">
                          <p>No import results available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </Modal>
          </div>
        )}
      </div>

      {/* Certification Details Modal */}
      <Modal
        isOpen={!!selectedCertificationsModal}
        onClose={() => setSelectedCertificationsModal(null)}
        title="Assigned Certifications"
        subtitle={selectedCertificationsModal ? `${selectedCertificationsModal.certifications.length} certification${selectedCertificationsModal.certifications.length !== 1 ? 's' : ''} assigned` : ''}
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedCertificationsModal(null)}
              className="px-4 py-2 bg-brand-amber-600 text-white rounded-lg hover:bg-brand-amber-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedCertificationsModal && (
          <div className="space-y-3">
            {selectedCertificationsModal.certifications.map((certification, idx) => (
              <div key={certification._id} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-8 h-8 bg-brand-amber-100 text-brand-amber-600 rounded-full flex items-center justify-center font-semibold text-sm">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{certification.name}</h3>
                  {/* <p className="text-xs text-gray-500 mt-1">Certification ID: {certification._id}</p> */}
                </div>
                <Award className="w-5 h-5 text-gray-400" />
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Assigned Users/Groups/Roles Modal */}
      <Modal
        isOpen={!!selectedAssignedUsersModal}
        onClose={() => setSelectedAssignedUsersModal(null)}
        title={
          selectedAssignedUsersModal?.type === 'USER' ? 'Assigned Users' :
          selectedAssignedUsersModal?.type === 'GROUP' ? 'Assigned Departments' :
          'Assigned Roles'
        }
        subtitle={
          selectedAssignedUsersModal?.type === 'USER'
            ? `${selectedAssignedUsersModal?.users?.length || 0} user${(selectedAssignedUsersModal?.users?.length || 0) !== 1 ? 's' : ''} assigned`
            : selectedAssignedUsersModal?.type === 'GROUP'
            ? `${selectedAssignedUsersModal?.groups?.length || 0} department${(selectedAssignedUsersModal?.groups?.length || 0) !== 1 ? 's' : ''} assigned`
            : `${selectedAssignedUsersModal?.roles?.length || 0} role${(selectedAssignedUsersModal?.roles?.length || 0) !== 1 ? 's' : ''} assigned`
        }
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedAssignedUsersModal(null)}
              className="px-4 py-2 bg-brand-amber-600 text-white rounded-lg hover:bg-brand-amber-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedAssignedUsersModal && (
          <div className="space-y-3">
            {/* Users List */}
            {selectedAssignedUsersModal.type === 'USER' && selectedAssignedUsersModal.users?.map((user) => (
              <div key={user._id} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  {user.name ? user.name.split(' ').map(n => n[0]).join('') : '?'}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{user.name || 'Unknown User'}</h3>
                  <p className="text-xs text-gray-500">{user.email || 'N/A'}</p>
                </div>
                <User className="w-5 h-5 text-gray-400" />
              </div>
            ))}

            {/* Groups List */}
            {selectedAssignedUsersModal.type === 'GROUP' && selectedAssignedUsersModal.groups?.map((group) => (
              <div key={group.group_key} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-purple-500 to-brand-pink-500 rounded-full flex items-center justify-center text-white">
                  <Users className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{group.group_name}</h3>
                  <p className="text-xs text-gray-500">{group.user_count} members</p>
                </div>
                <Users className="w-5 h-5 text-gray-400" />
              </div>
            ))}

            {/* Roles List */}
            {selectedAssignedUsersModal.type === 'ROLE' && selectedAssignedUsersModal.roles?.map((role) => (
              <div key={role.role_key} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-blue-500 to-brand-cyan-500 rounded-full flex items-center justify-center text-white">
                  <Shield className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{role.role_name}</h3>
                  <p className="text-xs text-gray-500">{role.user_count} users</p>
                </div>
                <Shield className="w-5 h-5 text-gray-400" />
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Skipped Users Modal */}
      <Modal
        isOpen={!!selectedSkippedUsersModal}
        onClose={() => setSelectedSkippedUsersModal(null)}
        title="Skipped Users/Groups/Roles"
        subtitle={selectedSkippedUsersModal ? `${(selectedSkippedUsersModal.skipped_users?.length || 0) + (selectedSkippedUsersModal.skipped_groups?.length || 0) + (selectedSkippedUsersModal.skipped_roles?.length || 0)} item${((selectedSkippedUsersModal.skipped_users?.length || 0) + (selectedSkippedUsersModal.skipped_groups?.length || 0) + (selectedSkippedUsersModal.skipped_roles?.length || 0)) !== 1 ? 's' : ''} skipped` : ''}
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedSkippedUsersModal(null)}
              className="px-4 py-2 bg-brand-amber-600 text-white rounded-lg hover:bg-brand-amber-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedSkippedUsersModal && (
          <div className="space-y-3">
            {/* Skipped Users List */}
            {selectedSkippedUsersModal.skipped_users && selectedSkippedUsersModal.skipped_users.length > 0 && (
              <>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Skipped Users</h4>
                {selectedSkippedUsersModal.skipped_users.map((user, userIdx) => (
                  <div key={userIdx} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-amber-500 to-brand-orange-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                      {user.user_name ? user.user_name.split(' ').map(n => n[0]).join('') : '?'}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900">{user.user_name || 'Unknown User'}</h3>
                      <p className="text-xs text-gray-500">{user.user_email || 'N/A'}</p>
                      <p className="text-xs text-red-500 mt-1">{user.reason}</p>
                    </div>
                    <User className="w-5 h-5 text-gray-400" />
                  </div>
                ))}
              </>
            )}

            {/* Skipped Groups List */}
            {selectedSkippedUsersModal.skipped_groups && selectedSkippedUsersModal.skipped_groups.length > 0 && (
              <>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Skipped Groups</h4>
                {selectedSkippedUsersModal.skipped_groups.map((group, groupIdx) => (
                  <div key={groupIdx} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-purple-500 to-brand-pink-500 rounded-full flex items-center justify-center text-white">
                      <Users className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900">{group.group_name || 'Unknown Group'}</h3>
                      <p className="text-xs text-gray-500">Group Key: {group.group_key}</p>
                      <p className="text-xs text-red-500 mt-1">{group.reason}</p>
                    </div>
                    <Users className="w-5 h-5 text-gray-400" />
                  </div>
                ))}
              </>
            )}

            {/* Skipped Roles List */}
            {selectedSkippedUsersModal.skipped_roles && selectedSkippedUsersModal.skipped_roles.length > 0 && (
              <>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Skipped Roles</h4>
                {selectedSkippedUsersModal.skipped_roles.map((role, roleIdx) => (
                  <div key={roleIdx} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-brand-blue-500 to-brand-cyan-500 rounded-full flex items-center justify-center text-white">
                      <Shield className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900">{role.role_name || 'Unknown Role'}</h3>
                      <p className="text-xs text-gray-500">Role Key: {role.role_key}</p>
                      <p className="text-xs text-red-500 mt-1">{role.reason}</p>
                    </div>
                    <Shield className="w-5 h-5 text-gray-400" />
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Revoke Assignment Confirmation Modal */}
      <Modal
        isOpen={!!revokeModal}
        onClose={() => !revokeLoading && setRevokeModal(null)}
        title="Revoke Assignment"
        size="md"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setRevokeModal(null)}
              disabled={revokeLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRevokeAssignment}
              disabled={revokeLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {revokeLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Revoking...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Revoke Assignment
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">
              This action cannot be undone. All related data will be permanently deleted.
            </p>
          </div>
          <p className="text-sm text-gray-700">
            Are you sure you want to revoke the {revokeModal?.type === 'GROUP' ? 'department' : 'role'} certification assignment for <span className="font-semibold text-gray-900">{revokeModal?.name}</span>?
          </p>
          <div className="text-sm text-gray-600 space-y-1">
            <p>The following will be deleted:</p>
            <ul className="list-disc list-inside text-gray-500 space-y-0.5">
              <li>Certification tracking records</li>
              <li>Exam attempts for affected users</li>
              <li>Issued certificates for affected users</li>
              <li>The assignment record itself</li>
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
}
